import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { IFinancialOperation } from "dto/Banking/IBanking";
import { IMergeHistoryEntry } from "dto/Contractors/IContractor";
import contractorsService from "api/contractorsService";
import styles from "./styles.module.scss";

// ── Typy ─────────────────────────────────────────────────────────────────────

export interface LinkedInvoice {
  id: string;
  number: string;
  date: string;
  grossAmount: number;
  currency: string;
  type: "Zakup" | "Sprzedaż" | "Rejestr";
  /** Numer KSeF — tylko faktury Zakup/Sprzedaż z KSeF. Używany do GET /ksef/preview?number= */
  ksefNumber?: string;
  /** URI zaszyfrowanego skanu — tylko faktury Rejestr z dołączonym plikiem. */
  fileUri?: string;
  encryptionKeyId?: string;
  fileName?: string;
  objectKeys?: import("dto/IKey").IKey[];
}

interface Props {
  ibans: string[];
  invoices: LinkedInvoice[];
  bankOps: IFinancialOperation[];
  contractorId: string;
  onUndoMerge: (mergeLogId: string, duplicateName: string) => Promise<void>;
}

// ── Pomocnicze ────────────────────────────────────────────────────────────────

const plDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

const fmtAmt = (v: number, currency = "PLN") =>
  new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) +
  " " + currency;

function fmtIban(raw: string): string {
  if (!raw) return "";
  const digits = raw.replace(/^PL/i, "").replace(/\D/g, "");
  if (digits.length !== 26) return raw;
  const groups = digits.match(/.{1,4}/g) ?? [];
  return `PL ${groups.join(" ")}`;
}

const TYPE_BADGE: Record<string, string> = {
  Zakup:    styles.badgeZakup,
  Sprzedaż: styles.badgeSprzedaz,
  Rejestr:  styles.badgeRejestr,
};

// ── Nagłówek sekcji akordeonu ─────────────────────────────────────────────────

interface SectionHeaderProps {
  label: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
}

const SectionHeader = ({ label, count, open, onToggle }: SectionHeaderProps) => (
  <button className={styles.sectionHeader} onClick={onToggle}>
    <FontAwesomeIcon
      icon={open ? faChevronDown : faChevronRight}
      className={styles.chevron}
    />
    <span className={styles.sectionLabel}>{label}</span>
    {count !== undefined && (
      <span className={styles.sectionCount}>{count}</span>
    )}
  </button>
);

// ── Sekcja historii scaleń ────────────────────────────────────────────────────

function fmtNip(n: string) {
  return /^\d{10}$/.test(n) ? `${n.slice(0,3)}-${n.slice(3,6)}-${n.slice(6,8)}-${n.slice(8)}` : n;
}

function fmtIbanShort(raw: string) {
  const digits = raw.replace(/^PL/i, "").replace(/\D/g, "");
  if (digits.length !== 26) return raw;
  return `PL …${digits.slice(-8)}`;
}

const MergeHistorySection = ({
  contractorId,
  onUndoMerge,
}: {
  contractorId: string;
  onUndoMerge: (mergeLogId: string, duplicateName: string) => Promise<void>;
}) => {
  const [open, setOpen] = useState(true);
  const [splitting, setSplitting] = useState<string | null>(null);
  const { data: history = [], isLoading } = contractorsService.useMergeHistory(contractorId);

  if (!isLoading && history.length === 0) return null;

  const handleSplit = async (entry: IMergeHistoryEntry) => {
    setSplitting(entry.MergeLogId);
    try {
      await onUndoMerge(entry.MergeLogId, entry.DuplicateName);
    } finally {
      setSplitting(null);
    }
  };

  return (
    <div className={styles.sectionFull} style={{ borderBottom: "1px solid #e8e4f4", paddingBottom: 4, marginBottom: 4 }}>
      <SectionHeader
        label="Scalone rekordy"
        count={isLoading ? undefined : history.length}
        open={open}
        onToggle={() => setOpen(v => !v)}
      />
      {open && (
        isLoading ? (
          <p className={styles.empty}>Ładowanie…</p>
        ) : (
          <div style={{ padding: "4px 4px 6px 22px", display: "flex", flexDirection: "column", gap: 4 }}>
            {history.map(entry => (
              <div
                key={entry.MergeLogId}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "6px 10px",
                  background: "#fff",
                  border: "1px solid #e8e4f4",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: "#1a1630", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {entry.DuplicateName}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 2, color: "#8a7fa5", flexWrap: "wrap" }}>
                    {entry.DuplicateNip && (
                      <span style={{ fontFamily: "monospace", fontSize: 11 }}>NIP {fmtNip(entry.DuplicateNip)}</span>
                    )}
                    {entry.DuplicateIban && (
                      <span style={{ fontFamily: "monospace", fontSize: 11 }}>{fmtIbanShort(entry.DuplicateIban)}</span>
                    )}
                    <span style={{ fontSize: 11 }}>
                      scalono {new Date(entry.MergedAt).toLocaleDateString("pl-PL")}
                    </span>
                    {entry.MovedRecordsTotal > 0 && (
                      <span style={{ fontSize: 11 }}>
                        · {entry.MovedRecordsTotal} dok. przepisano
                      </span>
                    )}
                  </div>
                </div>
                <Tooltip title={`Rozdziel — przywróć rekord „${entry.DuplicateName}" jako osobny wpis`} placement="top">
                  <button
                    disabled={splitting === entry.MergeLogId}
                    onClick={() => handleSplit(entry)}
                    style={{
                      display: "flex", alignItems: "center", gap: 5,
                      height: 28, padding: "0 10px",
                      borderRadius: 5,
                      border: "1px solid rgba(198,40,40,0.3)",
                      background: "#fff",
                      color: splitting === entry.MergeLogId ? "#bbb" : "#c62828",
                      fontSize: 12, fontWeight: 600,
                      cursor: splitting === entry.MergeLogId ? "not-allowed" : "pointer",
                      flexShrink: 0,
                      transition: "border-color 0.15s, background 0.15s",
                    }}
                  >
                    {splitting === entry.MergeLogId ? "Rozdzielam…" : "↩ Rozdziel"}
                  </button>
                </Tooltip>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

// ── Komponent główny ──────────────────────────────────────────────────────────

const ContractorDetailPanel = ({ ibans, invoices, bankOps, contractorId, onUndoMerge }: Props) => {
  const [openIbans,    setOpenIbans]    = useState(true);
  const [openInvoices, setOpenInvoices] = useState(true);
  const [openBankOps,  setOpenBankOps]  = useState(true);

  return (
    <div className={styles.panel}>

      {/* ── Historia scaleń — pojawia się tylko gdy istnieją wpisy ─────────── */}
      <MergeHistorySection contractorId={contractorId} onUndoMerge={onUndoMerge} />

      {/* ── Rachunki bankowe (tylko po scaleniu z wieloma IBAN-ami) ─────────── */}
      {ibans.length > 1 && (
        <div className={styles.sectionFull}>
          <SectionHeader
            label="Rachunki bankowe"
            count={ibans.length}
            open={openIbans}
            onToggle={() => setOpenIbans((v) => !v)}
          />
          {openIbans && (
            <div className={styles.ibanList}>
              {ibans.map((ib, i) => (
                <span key={ib} className={styles.ibanItem}>
                  {i === 0 && <span className={styles.ibanPrimary}>główny</span>}
                  <span className={styles.ibanValue}>{fmtIban(ib)}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Sekcje 1–4 w siatce 2×2 ─────────────────────────────────────────── */}
      <div className={styles.grid2x2}>

        {/* Sekcja 1: Faktury i rachunki */}
        <div className={styles.cell}>
          <SectionHeader
            label="Faktury i rachunki"
            count={invoices.length}
            open={openInvoices}
            onToggle={() => setOpenInvoices((v) => !v)}
          />
          {openInvoices && (
            invoices.length === 0 ? (
              <p className={styles.empty}>Brak powiązanych faktur</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Nr dokumentu</th>
                    <th style={{ textAlign: "right" }}>Kwota brutto</th>
                    <th>Typ</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td>{plDate(inv.date)}</td>
                      <td className={styles.monoCell}>{inv.number || "—"}</td>
                      <td className={styles.amount}>
                        {inv.grossAmount ? fmtAmt(inv.grossAmount, inv.currency) : "—"}
                      </td>
                      <td>
                        <span className={`${styles.badge} ${TYPE_BADGE[inv.type] ?? ""}`}>
                          {inv.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>

        {/* Sekcja 2: Operacje finansowe */}
        <div className={styles.cell}>
          <SectionHeader
            label="Operacje finansowe"
            count={bankOps.length}
            open={openBankOps}
            onToggle={() => setOpenBankOps((v) => !v)}
          />
          {openBankOps && (
            bankOps.length === 0 ? (
              <p className={styles.empty}>Brak powiązanych przelewów</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th style={{ textAlign: "right" }}>Kwota</th>
                    <th>Kierunek</th>
                    <th>Tytuł</th>
                  </tr>
                </thead>
                <tbody>
                  {bankOps.map((op) => {
                    const isCredit = op.Direction === "Credit";
                    return (
                      <tr key={op.Id}>
                        <td>{plDate(op.BookingDate ?? "")}</td>
                        <td className={`${styles.amount} ${isCredit ? styles.amountCredit : styles.amountDebit}`}>
                          {isCredit ? "+" : "−"}{fmtAmt(op.Amount, op.Currency)}
                        </td>
                        <td>
                          <span className={`${styles.badge} ${isCredit ? styles.badgeWplyw : styles.badgeWyplyw}`}>
                            {isCredit ? "Wpływ" : "Wypływ"}
                          </span>
                        </td>
                        <td className={styles.titleCell} title={op.Title ?? ""}>
                          {op.Title || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
        </div>

      </div>
    </div>
  );
};

export default ContractorDetailPanel;
